/**
 * The AI pilot: a small state machine over the manoeuvre vocabulary, plus
 * trigger discipline.
 *
 * It sees exactly what a pilot sees — its own aircraft state and the other
 * aircraft's pose and motion — and it produces exactly what a pilot produces:
 * one `FlightInputs` per tick. Every random choice draws from the `ai` stream
 * and only at a decision point, so a replay picks the same manoeuvres.
 *
 * The commitment rule is what makes it read as a pilot rather than a
 * controller: once a manoeuvre is chosen it is flown for its own duration
 * (`MANEUVERS[id].durationSeconds`, scaled by how quickly this skill tier
 * reassesses) unless something urgent — the ground, a spin, a fresh burst into
 * the cockpit — overrides it.
 *
 * A pilot may also carry a **route** (`route.ts`). A routed pilot flies its
 * legs until something engages it, fights exactly as an unrouted one does while
 * engaged, and picks the route back up when the sky is clear again. The two
 * ranges are deliberately different (`aiEngageRangeM` < `aiDisengageRangeM`):
 * with one threshold a flight sitting at the boundary flickers between
 * navigating and fighting every tick.
 *
 * What it is *pointed at* is a `CombatBody`, not another aeroplane. That is what
 * lets a flight sent to burn a balloon actually attack it: the geometry, the
 * lead solution and the trigger all only ever wanted a pose and a velocity, and
 * insisting on a `SimState` was the single reason BALLOON DEFENSE could not be
 * flown. Who the body belongs to is the roster's business (`encounter-tick.ts`),
 * not the pilot's.
 */

import type { FlightInputs } from "../flight/inputs.ts";
import { NEUTRAL_INPUTS } from "../flight/inputs.ts";
import { controlsLocked } from "../flight/physiology.ts";
import type { RngBank } from "../flight/rng.ts";
import type { SimState } from "../flight/state.ts";
import type { Tunables } from "../flight/tunables.ts";
import { inverseRotateVec3 } from "../flight/math3d.ts";
import { climbingOut, needsRecovery, recoveryInputs, takeoffInputs, takingOff } from "./autopilot.ts";
import { assessThreat, classifySituation, isDefensive, type ThreatAssessment } from "./ai-situation.ts";
import { pickEvasion, type EvadeSituation } from "./evade-table.ts";
import { aimPoint, bodyOf, relativeGeometry, subtractVec3, type CombatBody, type RelativeGeometry } from "./geometry.ts";
import { gunsBear } from "./traverse.ts";
import {
  MANEUVERS,
  maneuverInputs,
  type ManeuverCommand,
  type ManeuverContext,
} from "./maneuvers.ts";
import { currentWaypoint, holdCourseInputs, routeInputs, type RouteOrders, type RouteState } from "./route.ts";
import { canFly, skillProfile, type AiSkill, type SkillProfile } from "./skill.ts";

export const AI_MODES = ["takeoff", "patrol", "pursue", "evade", "recover", "route"] as const;

export type AiMode = (typeof AI_MODES)[number];

export interface AiPilotState {
  readonly skill: AiSkill;
  readonly mode: AiMode;
  readonly command: ManeuverCommand;
  /** Seconds flown on the current manoeuvre. */
  readonly maneuverSeconds: number;
  /** Seconds spent in the current trigger phase (holding or resting). */
  readonly triggerSeconds: number;
  readonly firing: boolean;
  /**
   * The aiming mistake this pilot is currently carrying, radians, redrawn at
   * each manoeuvre decision. It is what skill *is* now that rounds fly straight
   * and hits are geometry — see `skill.ts`.
   */
  readonly aimErrorAzRad: number;
  readonly aimErrorElRad: number;
}

export function createAiPilot(skill: AiSkill): AiPilotState {
  return {
    skill,
    mode: "patrol",
    command: { id: "extend", direction: 1 },
    maneuverSeconds: 0,
    triggerSeconds: 0,
    firing: false,
    aimErrorAzRad: 0,
    aimErrorElRad: 0,
  };
}

/** A fresh aiming mistake for this pilot: two draws on the `ai` stream, magnitude then bearing. */
function drawAimError(profile: SkillProfile, view: AiView): { aimErrorAzRad: number; aimErrorElRad: number } {
  const magnitude = view.tunables.aiAimErrorRad * Math.max(0, profile.aimErrorScale) * view.rng.nextFloat("ai");
  const phi = 2 * Math.PI * view.rng.nextFloat("ai");
  return { aimErrorAzRad: magnitude * Math.cos(phi), aimErrorElRad: magnitude * Math.sin(phi) };
}

export interface AiView {
  readonly self: SimState;
  /** Whatever this pilot is after, as a pose and a velocity: an aeroplane, a balloon, a Zeppelin. */
  readonly target: CombatBody | null;
  /** True on any tick this aircraft took rounds. */
  readonly underFire: boolean;
  readonly tunables: Tunables;
  readonly rng: RngBank;
  readonly dt: number;
  /** The flight's navigation, already advanced for this tick, or `null` for a pilot with nowhere to be. */
  readonly route?: RouteState | null;
  /** Whether this flight may break off its route to fight. Defaults to `engage`. */
  readonly routeOrders?: RouteOrders;
}

export interface AiDecision {
  readonly pilot: AiPilotState;
  readonly inputs: FlightInputs;
}

/** Break *into* the attacker: toward whichever side it is on, right if dead astern. */
function breakDirection(geometry: RelativeGeometry | null): number {
  if (!geometry || geometry.bearingRad === 0) return 1;
  return geometry.bearingRad > 0 ? 1 : -1;
}

function firstFlyable(profile: SkillProfile, candidates: readonly ManeuverCommand[]): ManeuverCommand {
  return candidates.find((candidate) => canFly(profile, candidate.id)) ?? { id: "pursue", direction: 1 };
}

/**
 * The offensive choice. Energy first (a scout that has thrown its speed away
 * cannot fight), then geometry: overshooting wants a barrel roll, a target far
 * above wants a zoom, a target far below wants a dive, anything else is a lead
 * turn onto the gun solution.
 */
function pursueCommand(
  profile: SkillProfile,
  geometry: RelativeGeometry,
  self: SimState,
  tunables: Tunables,
): ManeuverCommand {
  const direction = breakDirection(geometry);
  const speedFraction = self.readout.airspeedMps / Math.max(self.config.spec.vmaxMps, 1);
  const overshooting = geometry.rangeM < tunables.aiMinFiringRangeM * 3 && geometry.closureMps > 25;
  if (overshooting) return firstFlyable(profile, [{ id: "barrelRoll", direction }, { id: "breakTurn", direction }]);
  if (speedFraction < 0.5 && geometry.heightDifferenceM < -150) {
    return firstFlyable(profile, [{ id: "dive", direction }]);
  }
  if (geometry.heightDifferenceM > 200 && speedFraction > 0.75) {
    return firstFlyable(profile, [{ id: "zoom", direction }, { id: "pursue", direction }]);
  }
  return firstFlyable(profile, [{ id: "leadTurn", direction }, { id: "pursue", direction }]);
}

interface Assessment {
  readonly geometry: RelativeGeometry | null;
  readonly threat: ThreatAssessment | null;
  readonly situation: EvadeSituation;
  readonly profile: SkillProfile;
}

function assess(state: AiPilotState, view: AiView): Assessment {
  const profile = skillProfile(state.skill);
  const geometry = view.target ? relativeGeometry(bodyOf(view.self), view.target) : null;
  const threat = geometry ? assessThreat(geometry, view.tunables) : null;
  return {
    geometry,
    threat,
    profile,
    situation: classifySituation({
      self: view.self,
      geometry,
      threat,
      underFire: view.underFire,
      tunables: view.tunables,
    }),
  };
}

/** Does this tick end the current manoeuvre and call for a new decision? */
function decisionDue(state: AiPilotState, assessment: Assessment, view: AiView): boolean {
  const descriptor = MANEUVERS[state.command.id];
  const commitment = descriptor.durationSeconds * assessment.profile.decisionIntervalScale;
  const recovering = needsRecovery(view.self, view.tunables);
  // Reaching this function in `route` mode means the flight has just been
  // engaged: there is no manoeuvre in progress to honour, so decide now rather
  // than flying out an `extend` the route branch only ever used as a placeholder.
  if (state.mode === "route") return true;
  // A spin, a stall or the approaching ground interrupts whatever was being
  // flown; so does the first burst that arrives while attacking.
  if (recovering !== (state.mode === "recover")) return true;
  if (state.maneuverSeconds >= commitment) return true;
  if (view.underFire && state.mode !== "evade") return true;
  return descriptor.needsTarget && view.target === null;
}

function chooseCommand(assessment: Assessment, view: AiView): { mode: AiMode; command: ManeuverCommand } {
  const { profile, geometry, situation } = assessment;
  if (needsRecovery(view.self, view.tunables)) {
    return { mode: "recover", command: { id: "extend", direction: breakDirection(geometry) } };
  }
  const defensive = isDefensive(situation) || situation === "damaged";
  // A pilot who is winning presses on. `aggression` is the chance a defensive
  // read is overridden — but never when rounds are actually arriving or the
  // enemy already has a firing solution, which no tier is brave enough to ignore.
  const forced = view.underFire || situation === "threatGuns";
  const presses = view.rng.nextFloat("ai") < profile.aggression && !forced;
  if (defensive && !presses) {
    return { mode: "evade", command: pickEvasion(profile, situation, breakDirection(geometry), view.rng) };
  }
  if (situation === "lowAltitude") {
    const direction = breakDirection(geometry);
    return { mode: "evade", command: firstFlyable(profile, [{ id: "zoom", direction }, { id: "extend", direction }]) };
  }
  if (!geometry) return { mode: "patrol", command: { id: "extend", direction: 1 } };
  return { mode: "pursue", command: pursueCommand(profile, geometry, view.self, view.tunables) };
}

interface Trigger {
  readonly firing: boolean;
  readonly triggerSeconds: number;
}

/**
 * Trigger discipline. The manual is explicit that this is a skill: *"The great
 * pilots would fire their guns sparingly, closing to within 30 yards and firing
 * a short, decisive burst."* A novice's `burstScale` makes that burst three
 * times as long — and three times as likely to jam.
 */
/**
 * Can this aeroplane's guns actually be pointed at the target?
 *
 * Two ways to answer yes, and an aeroplane may have both. A **fixed** gun is
 * aimed by flying, so the question is the pilot's own firing cone and the
 * `.DAT` arc has nothing to say about it — that arc is a clamp on the round,
 * not a permission to shoot. A **gunner's** station answers on its traverse arc
 * alone, so a target astern is a shot even with the nose pointing the other
 * way.
 *
 * The three shapes that fall out are exactly the fleet's: a scout fires ahead
 * and nowhere else; an R.E.8 fires ahead *and* backwards, whichever bears; and
 * a B.E.2, which has no fixed gun at all, holds its fire at anything in front
 * and shoots at whatever gets on its tail. That last asymmetry is what makes an
 * escort mission behave — the two-seaters defend each other and cannot strafe.
 */
function gunsCanEngage(view: AiView, geometry: RelativeGeometry, profile: SkillProfile): boolean {
  const gun = view.self.config.spec.guns;
  if (gun.hasFixedGun && geometry.angleOffNoseRad < view.tunables.aiFiringConeRad * profile.firingConeScale) {
    return true;
  }
  if (!view.target || !gun.mounts.some((mount) => mount.flexible)) return false;
  const aim = aimPoint(bodyOf(view.self), view.target, view.tunables.gunMuzzleSpeedMps);
  return gunsBear(gun, inverseRotateVec3(view.self.orientation, subtractVec3(aim.point, view.self.position)));
}

function updateTrigger(state: AiPilotState, assessment: Assessment, view: AiView): Trigger {
  const { profile, geometry } = assessment;
  const { tunables } = view;
  // Firing is *not* gated on being on the offensive: a pilot breaking away who
  // gets a snapshot as the enemy crosses the nose takes it. Only the recovery
  // case (stalled, spinning, about to hit the ground) has the pilot's hands
  // fully occupied.
  const solution =
    geometry !== null &&
    state.mode !== "recover" &&
    gunsCanEngage(view, geometry, profile) &&
    geometry.rangeM > tunables.aiMinFiringRangeM &&
    geometry.rangeM < tunables.aiMaxFiringRangeM * profile.firingRangeScale;

  const elapsed = state.triggerSeconds + view.dt;
  if (state.firing) {
    if (!solution || elapsed >= tunables.aiBurstSeconds * profile.burstScale) {
      return { firing: false, triggerSeconds: 0 };
    }
    return { firing: true, triggerSeconds: elapsed };
  }
  if (solution && elapsed >= tunables.aiBurstGapSeconds) return { firing: true, triggerSeconds: 0 };
  return { firing: false, triggerSeconds: elapsed };
}

/** Presses per second the AI works the U key at when its guns jam. */
const JAM_CLEAR_PRESSES_PER_SECOND = 2;

/**
 * The AI's U key. `guns.ts` counts one clear attempt per *press*, so holding it
 * down is one attempt and nothing more; this pulses at a human rate off the
 * sim clock (deterministic, no extra state) rather than every other tick, which
 * would clear a jam almost instantly and then escalate it to permanent.
 */
function clearJamPulse(sim: SimState): boolean {
  if (!sim.guns.jammed) return false;
  return Math.floor(sim.timeS * JAM_CLEAR_PRESSES_PER_SECOND * 2) % 2 === 0;
}

function contextFor(state: AiPilotState, assessment: Assessment, view: AiView): ManeuverContext {
  return {
    sim: view.self,
    command: state.command,
    elapsedSeconds: state.maneuverSeconds,
    target: view.target,
    geometry: assessment.geometry,
    leadQuality: assessment.profile.leadQuality,
    aimErrorAzRad: state.aimErrorAzRad,
    aimErrorElRad: state.aimErrorElRad,
    tunables: view.tunables,
  };
}

/**
 * Whether a routed pilot should be navigating rather than fighting. Asymmetric
 * by design: breaking off takes a contact inside `aiEngageRangeM`, rejoining
 * takes the sky being clear out to the wider `aiDisengageRangeM`.
 */
function staysOnRoute(state: AiPilotState, view: AiView): boolean {
  if (needsRecovery(view.self, view.tunables)) return false;
  // A bomber under `defend` orders is as committed to its route as a recon crew
  // under `pressOn`; the difference is only whether its gunners are working.
  if (view.routeOrders === "pressOn" || view.routeOrders === "defend") return true;
  if (view.underFire) return false;
  if (!view.target) return true;
  const range = Math.hypot(
    view.target.position.x - view.self.position.x,
    view.target.position.y - view.self.position.y,
    view.target.position.z - view.self.position.z,
  );
  // `patrol` counts as cruising, not fighting: it is the mode a pilot starts in
  // and the one it falls back to with nothing in sight, so a flight on its first
  // tick must use the same threshold it would use on its thousandth.
  const cruising = state.mode === "route" || state.mode === "patrol";
  const threshold = cruising ? view.tunables.aiEngageRangeM : view.tunables.aiDisengageRangeM;
  return range > threshold;
}

/**
 * The navigating branch: fly the leg, no manoeuvre commitment to carry.
 *
 * The guns are cold unless the flight's orders are `defend`, in which case the
 * ordinary trigger discipline runs against the same assessment an engaged pilot
 * would make. That is exactly what a bomber's gunners do — they shoot at
 * whatever comes inside their arcs while the pilot holds the run.
 */
function flyRoute(state: AiPilotState, view: AiView, route: RouteState, defending: Assessment | null): AiDecision {
  const waypoint = currentWaypoint(route);
  const stick = waypoint
    ? routeInputs(view.self, waypoint, view.tunables)
    : holdCourseInputs(view.self, view.tunables);
  const navigating: AiPilotState = {
    ...state,
    mode: "route",
    command: { id: "extend", direction: 1 },
    maneuverSeconds: 0,
  };
  const trigger = defending ? updateTrigger(navigating, defending, view) : { firing: false, triggerSeconds: 0 };
  return {
    pilot: { ...navigating, firing: trigger.firing, triggerSeconds: trigger.triggerSeconds },
    inputs: { ...stick, firing: trigger.firing, clearJam: clearJamPulse(view.self) },
  };
}

/**
 * The take-off branch: guns cold, no manoeuvre committed, the aeroplane flown
 * off the field and up to a safe height before anything else is considered.
 * Nothing else in the AI can run while the wheels are down — every other branch
 * assumes an aeroplane that is flying — and the mode is *latched* through the
 * climb-out, because "low and climbing" on its own describes a dogfight as
 * readily as a departure. Entry is the wheels being down; exit is
 * `climbingOut` saying the aeroplane is clear.
 */
function flyTakeoff(state: AiPilotState, view: AiView): AiDecision {
  return {
    pilot: {
      ...state,
      mode: "takeoff",
      command: { id: "extend", direction: 1 },
      maneuverSeconds: 0,
      triggerSeconds: 0,
      firing: false,
    },
    inputs: takeoffInputs(view.self, view.tunables),
  };
}

/**
 * True while this pilot is committed to a departure — entered on the ground,
 * held through the climb-out. Rounds arriving end it: a scramble that is jumped
 * on the way up has a fight on its hands whether or not it has finished
 * climbing. Nothing ends it while the wheels are still down, because there is
 * nothing else an aeroplane on the ground can do.
 */
function departing(state: AiPilotState, view: AiView): boolean {
  if (takingOff(view.self)) return true;
  if (view.underFire) return false;
  return state.mode === "takeoff" && climbingOut(view.self, view.tunables);
}

/** One tick of AI: reassess, maybe re-decide, fly the manoeuvre, work the trigger. */
export function updateAiPilot(state: AiPilotState, view: AiView): AiDecision {
  if (controlsLocked(view.self.pilot) || view.self.crashed) {
    return { pilot: { ...state, firing: false, triggerSeconds: 0 }, inputs: NEUTRAL_INPUTS };
  }
  if (departing(state, view)) return flyTakeoff(state, view);

  const route = view.route ?? null;
  if (route && staysOnRoute(state, view)) {
    return flyRoute(state, view, route, view.routeOrders === "defend" ? assess(state, view) : null);
  }

  const assessment = assess(state, view);
  const redecide = decisionDue(state, assessment, view);
  const chosen = redecide ? chooseCommand(assessment, view) : { mode: state.mode, command: state.command };
  // Drawn after `chooseCommand`, so the `ai` stream's order is "what to fly,
  // then how well I am pointing it" and stays fixed whatever the pilot picks.
  const aimError = redecide ? drawAimError(assessment.profile, view) : {
    aimErrorAzRad: state.aimErrorAzRad,
    aimErrorElRad: state.aimErrorElRad,
  };
  const flying: AiPilotState = {
    ...state,
    ...aimError,
    mode: chosen.mode,
    command: chosen.command,
    maneuverSeconds: redecide ? view.dt : state.maneuverSeconds + view.dt,
  };

  const trigger = updateTrigger(flying, assessment, view);
  const stick =
    flying.mode === "recover"
      ? recoveryInputs(view.self, view.tunables)
      : maneuverInputs(contextFor(flying, assessment, view));

  return {
    pilot: { ...flying, firing: trigger.firing, triggerSeconds: trigger.triggerSeconds },
    inputs: { ...stick, firing: trigger.firing, clearJam: clearJamPulse(view.self) },
  };
}
